import { forwardRef } from '@chakra-ui/system';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';

import type { CircularProgressProps } from '@frontend/components';
import {
  Tooltip,
  Box,
  CircularProgress,
  useTheme,
  CheckmarkIcon,
} from '@frontend/components';

export const PROGRESS_STATES = {
  IDLE: 'Idle',
  WORKING: 'Working',
  WORKING_AI: 'WorkingAI',
  AI_COMPLETED: 'AICompleted',
} as const;

export type ProgressState =
  (typeof PROGRESS_STATES)[keyof typeof PROGRESS_STATES];

export type ProgressStepConfig = {
  key: ProgressState;
  label: string;
};

export const STATE_CONFIG: Record<ProgressState, ProgressStepConfig> = {
  [PROGRESS_STATES.IDLE]: {
    key: PROGRESS_STATES.IDLE,
    label: 'Idle',
  },
  [PROGRESS_STATES.WORKING]: {
    key: PROGRESS_STATES.WORKING,
    label: 'Processing...',
  },
  [PROGRESS_STATES.WORKING_AI]: {
    key: PROGRESS_STATES.WORKING_AI,
    label: 'Scanning content...',
  },
  [PROGRESS_STATES.AI_COMPLETED]: {
    key: PROGRESS_STATES.AI_COMPLETED,
    label: 'Done',
  },
};

export type ContainerActivityCircularProgressSizes = 'sm' | 'md' | 'lg';

export type ContainerActivityCircularProgressProps = Omit<
  CircularProgressProps,
  'value' | 'color'
> & {
  state: ProgressState;
  size?: ContainerActivityCircularProgressSizes | number;
  glowSize?: number;
  onStateChange?: (state: ProgressState) => void;
  alwaysShowTooltip?: boolean;
  label?: string;
  justFinishedDurationMs?: number;
  isTooltipDisabled?: boolean;
};

export const ContainerActivityCircularProgress = forwardRef<
  ContainerActivityCircularProgressProps,
  'div'
>(
  (
    {
      state,
      size = 'md',
      glowSize: glowSizeProp,
      alwaysShowTooltip,
      label,
      onStateChange,
      justFinishedDurationMs = 3000,
      isTooltipDisabled,
      ...progressProps
    },
    ref,
  ) => {
    const theme = useTheme();
    const config = STATE_CONFIG[state];

    let iconSize = 2.5;

    if (typeof size === 'number') {
      iconSize = size;
    } else {
      switch (size) {
        case 'sm':
          iconSize = 1.5;
          break;
        case 'md':
          iconSize = 2.5;
          break;
        case 'lg':
          iconSize = 4.5;
          break;
        default:
          break;
      }
    }

    const defaultGlowSize = `calc((${iconSize}rem * 3) + 4rem)`;
    const actualGlowSize =
      glowSizeProp !== undefined ? glowSizeProp : defaultGlowSize;
    const assistanceColor = theme.colors.alert?.assistance;
    const shouldHighlightAI =
      state === PROGRESS_STATES.WORKING_AI ||
      state === PROGRESS_STATES.AI_COMPLETED;
    const circularProgressColor = shouldHighlightAI
      ? assistanceColor
      : 'feature.secondary';

    useEffect(() => {
      if (state === PROGRESS_STATES.AI_COMPLETED) {
        const timer = setTimeout(() => {
          onStateChange?.(PROGRESS_STATES.IDLE);
        }, justFinishedDurationMs);

        return () => clearTimeout(timer);
      }
    }, [state, onStateChange, justFinishedDurationMs]);

    return (
      <Box
        ref={ref}
        display="inline-flex"
        placeItems="center"
        boxSize={iconSize}
        position="relative"
        zIndex={0}
        sx={{
          '&:after': {
            zIndex: -1,
            content: "''",
            position: 'absolute',
            pointerEvents: 'none',
            width: actualGlowSize,
            aspectRatio: '1 / 1',
            backgroundImage: `radial-gradient(closest-side, ${assistanceColor}, transparent)`,
            top: '50%',
            left: '50%',
            transform: shouldHighlightAI
              ? 'translate(-50%, -50%) scale(1)'
              : 'translate(-50%, -50%) scale(0)',
            opacity: shouldHighlightAI ? 0.25 : 0,
            transition:
              // When idle, fade out the glow slowly
              state === PROGRESS_STATES.IDLE
                ? 'opacity 3s ease-in-out, transform 0s 3s ease-in-out'
                : // Otherwise, transition normally
                  'opacity 1s ease-in-out, transform 1s ease-in-out',
          },
        }}
        {...progressProps}
      >
        <AnimatePresence initial={false}>
          {state === PROGRESS_STATES.IDLE ? null : (
            <Tooltip
              label={label || config.label}
              shouldWrapChildren
              isOpen={
                alwaysShowTooltip &&
                (state === PROGRESS_STATES.WORKING ||
                  state === PROGRESS_STATES.WORKING_AI ||
                  state === PROGRESS_STATES.AI_COMPLETED)
              }
              isDisabled={isTooltipDisabled}
            >
              <Box
                as={motion.div}
                key={state}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
              >
                {state === PROGRESS_STATES.AI_COMPLETED ? (
                  <CheckmarkIcon
                    size={iconSize}
                    color={circularProgressColor}
                  />
                ) : (
                  <CircularProgress
                    size={iconSize}
                    isIndeterminate
                    trackColor="transparent"
                    color={circularProgressColor}
                    display="flex"
                    placeItems="center"
                  />
                )}
              </Box>
            </Tooltip>
          )}
        </AnimatePresence>
      </Box>
    );
  },
);

ContainerActivityCircularProgress.displayName =
  'ContainerActivityCircularProgress';
